import type { FC } from 'react';
import { memo, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { PageLoadMetric } from '@atlassian/browser-metrics';

import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { resumeLowPriorityEvents } from '@confluence/analytics-event-delay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { browserMetricsState, markBrowserMetricEnd } from './utils/index.ts';
import { reportHydrationError } from './utils/reportHydrationError.ts';

export type Props = {
	metric: PageLoadMetric;
	customData?: any;
	stopTime?: number | null;
};

const Bm3PageLoadEndComponent: FC<Props> = memo(({ metric, customData = {}, stopTime }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const stopArguments = {
			stopTime: typeof stopTime === 'number' ? stopTime : undefined,
			customData: {
				...customData,
				previousRoute: browserMetricsState.previousRoute,
				pageLoadInfo: getUniquePageLoadId(),
			},
		};

		metric.stop(stopArguments);
		markBrowserMetricEnd(metric);

		void resumeLowPriorityEvents();

		// Not adding exhaustive dependencies because we want this to fire only once,
		// despite any changes to `customData` or `stopTime`
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [metric]);

	reportHydrationError(createAnalyticsEvent, metric?.key);

	return null;
});

/**
 * @deprecated This is the legacy BM3 implementation. Use `PageLoadEnd` from
 * `@confluence/browser-metrics/entry-points/PageLoadEnd` instead, which routes
 * through the interop layer and gates on `confluence_fe_deprecate_bm3`.
 */
export const Bm3PageLoadEnd = withTransparentErrorBoundary({
	attribution: Attribution.BACKBONE,
})(Bm3PageLoadEndComponent);
